import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { DialogPlugin, Dropdown, Form, Input, MessagePlugin, Space, Table } from 'tdesign-react';
import { isEmpty, round } from 'lodash';
import { useMemoizedFn } from 'ahooks';
import { connect } from 'react-redux';
import ShadowCard from '@/components/ShadowCard';
import SvgIcon from '@/components/SvgIcon';
import { gcTrigger, getBookkeeperList } from '@/services/kopBookkeeperService';
import { renderByteRate, renderDateTime, renderNumRate } from '@/utils/common';
import ColTip from '@/components/Tip';
import useTable from '@/hooks/useTable';
import useDisableButton from '@/components/DisableWrapper/useDisableButton';
import Dashboard from '../Dashboard';
import Detail from './Detail';
import GCDettail from './GCDetail';

const { FormItem } = Form;

function Body({ clusterName, deployEnv, perms }) {
  const [visible, setVisible] = useState(false);
  const [currentRow, setCurrentRow] = useState({});
  const [gcVisible, setGcVisible] = useState(false);
  const confirmButton = useDisableButton({ content: '确定' });

  const options = [
    {
      content: '查看GC',
      resource: 'kop:bookkeeper:list:detail',
      value: 1,
    },
    {
      content: 'GC触发',
      resource: 'kop:bookkeeper:list:trigger',
      value: 2,
    },
  ].filter((item) => perms.includes(item.resource));

  const columns = [
    {
      colKey: 'host',
      title: '地址',
      sorter: true,
      fixed: 'left',
      cell: ({ row }) => <Detail detail={row} clusterName={clusterName} />,
      width: 120,
    },
    {
      colKey: 'port',
      title: '端口',
      sorter: true,
      width: 110,
      cell: ({ row }) => `${row.port}, ${row.metricsPort}`,
    },
    {
      colKey: 'diskUsage',
      title: '磁盘占比',
      sorter: true,
      width: 120,
      cell: ({ row }) => `${round(row.diskUsage, 2)}%`,
    },
    {
      colKey: 'bytesIn',
      title: '写入吞吐',
      cell: renderByteRate,
      width: 120,
      sorter: true,
    },
    {
      colKey: 'bytesOut',
      title: '读取吞吐',
      width: 120,
      cell: renderByteRate,
      sorter: true,
    },
    {
      colKey: 'rateIn',
      title: '写入速率',
      width: 120,
      cell: renderNumRate,
      sorter: true,
    },
    {
      colKey: 'rateOut',
      title: '读取速率',
      width: 120,
      cell: renderNumRate,
      sorter: true,
    },
    {
      colKey: 'version',
      title: '版本',
      sorter: true,
      width: 100,
    },
    // {
    //   colKey: 'ledgerCount',
    //   title: 'Ledgers',
    //   width: 100,
    //   sorter: true,
    //   cell: renderNum,
    // },
    {
      colKey: 'createTime',
      title: <ColTip content='第一次采集时间'>创建时间</ColTip>,
      sorter: true,
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'updateTime',
      title: <ColTip content='非实时，有分钟级延迟'>采集时间</ColTip>,
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Space size='small'>
          <Icon
            type='presentation-chart-line'
            size={18}
            onClick={() => {
              setVisible(true);
              setCurrentRow(row);
            }}
          />
          {deployEnv !== 'container' && !isEmpty(options) && (
            <Dropdown options={options} onClick={({ value }) => clickHandler(value, row)}>
              <span>
                <Icon size={18} type='dots-vertical' />
              </span>
            </Dropdown>
          )}
        </Space>
      ),
      width: 80,
    },
  ];

  const loadData = useMemoizedFn(async (params = {}) => {
    const { data } = await getBookkeeperList({ ...params, clusterName });
    setData(data.bookieMetricsList);
  });

  const { header, setData, config, doSearch } = useTable({
    search: (
      <FormItem name='host'>
        <Input placeholder='请输入Bookie地址' clearable />
      </FormItem>
    ),
    onSearch: loadData,
    columns,
  });

  useEffect(() => {
    if (clusterName) {
      doSearch();
    }
  }, [clusterName, doSearch]);

  const handleClose = () => {
    setVisible(false);
  };

  const clickHandler = async (value, row) => {
    setCurrentRow(row);
    if (value === 1) {
      // 查看GC
      setGcVisible(true);
    }
    if (value === 2) {
      // 触发GC
      const dialog = DialogPlugin.confirm({
        header: '触发GC？',
        theme: 'warning',
        body: 'Bookie中的数据删除由垃圾回收任务触发，默认情况下按照一定的周期执行，如需要紧急删除，可执行该方法。',
        confirmBtn: confirmButton,
        onCancel: () => dialog.hide(),
        onCloseBtnClick: () => dialog.hide(),
        onOverlayClick: () => dialog.hide(),
        onEscKeydown: () => dialog.hide(),
        onConfirm: async () => {
          try {
            dialog.update({
              confirmBtn: { loading: true, content: '确定' },
            });
            await gcTrigger(row.host, row.metricsPort, { clusterName });
            MessagePlugin.success('操作成功！');
            dialog.hide();
          } catch (error) {
            console.trace(error);
            throw error;
          } finally {
            dialog.update({
              confirmBtn: { loading: false, content: '确定' },
            });
          }
        },
      });
    }
  };

  if (!clusterName) return null;

  return (
    <Root>
      {header}
      <Table {...config} />
      <Dashboard
        clusterName={clusterName}
        detail={currentRow}
        visible={visible}
        handleClose={handleClose}
      />
      <GCDettail
        detail={currentRow}
        visible={gcVisible}
        clusterName={clusterName}
        onClose={() => setGcVisible(false)}
      />
    </Root>
  );
}

export default connect((state) => ({
  perms: state.auth.perms,
  deployEnv: state.kopCluster.detail.deployEnv,
}))(Body);

const Root = styled(ShadowCard)`
  flex: 1;
  margin-top: 12px;
`;

const Icon = styled(SvgIcon)`
  cursor: pointer;
  color: #194dff;
`;
